import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  PassOrderPageAvailabilityOut,
  PassOrderPageData,
  PassScheduleSelectionDTO,
  PassService,
} from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getOrderPage } from '../../../../utils/AccountUtils';
import { getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { formatSecondsToTime, getDayName } from '../../../../utils/weekdayFormat';

type Props = {
  passOrderPage: PassOrderPageData;
  accountId: string;
  value: PassScheduleSelectionDTO[];
  onChange: (selections: PassScheduleSelectionDTO[]) => void;
  refreshKey?: number;
  showHeader?: boolean;
  minSelections?: number;
  maxSelections?: number;
};

const slotKey = (orderPageId: string, weekday: string, startTime: number) => `${orderPageId}|${weekday}|${startTime}`;

const toSelectedKeys = (selections: PassScheduleSelectionDTO[]): Set<string> =>
  new Set(selections.map((s) => slotKey(s.order_page_id, s.weekday, s.start_time)));

const fromSelectedKeys = (keys: Set<string>): PassScheduleSelectionDTO[] =>
  Array.from(keys).map((key) => {
    const [order_page_id, weekday, startTime] = key.split('|');
    return { order_page_id, weekday, start_time: Number(startTime) };
  });

export const FixedSchedulePicker = ({
  passOrderPage,
  accountId,
  value,
  onChange,
  refreshKey,
  showHeader,
  minSelections,
  maxSelections,
}: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);

  const weeklyCount = passOrderPage.weekly_session_count ?? 0;
  const minCount = minSelections ?? weeklyCount;
  const maxCount = maxSelections ?? weeklyCount;
  const isUnbounded = !Number.isFinite(maxCount);
  const activityLabelPlural = account.activity_label_plural?.[language] ?? t('passes.activity_fallback_plural');
  const activityLabelSingular = account.activity_label_singular?.[language] ?? t('passes.activity_fallback_singular');

  const [availability, setAvailability] = useState<PassOrderPageAvailabilityOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const selectedKeys = useMemo(() => toSelectedKeys(value), [value]);

  useEffect(() => {
    let cancelled = false;
    const fetchAvailability = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await PassService.getPassAvailability(passOrderPage.id, accountId);
        if (!cancelled) setAvailability(data);
      } catch {
        if (!cancelled) setLoadError(t('saveHeader.errorGeneric'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchAvailability();
    return () => {
      cancelled = true;
    };
  }, [passOrderPage.id, accountId, refreshKey, t]);

  const toggleSlot = (orderPageId: string, weekday: string, startTime: number) => {
    const key = slotKey(orderPageId, weekday, startTime);
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      if (!isUnbounded && next.size >= maxCount) return;
      next.add(key);
    }
    onChange(fromSelectedKeys(next));
  };

  const pickPrompt = isUnbounded
    ? t('passes.change_schedule_pick_slots_flexible', { activities: activityLabelPlural })
    : minCount === maxCount
    ? minCount === 1
      ? t('passes.change_schedule_pick_slots_one', { activity: activityLabelSingular })
      : t('passes.change_schedule_pick_slots_other', { count: maxCount, activities: activityLabelPlural })
    : t('passes.change_schedule_pick_slots_range', {
        min: minCount,
        max: maxCount,
        activities: activityLabelPlural,
      });

  const selectionInRange = selectedKeys.size >= minCount && (isUnbounded || selectedKeys.size <= maxCount);
  const selectionChipLabel = isUnbounded
    ? t('passes.selected_count', { count: selectedKeys.size })
    : t('passes.selected_of_total', { count: selectedKeys.size, total: maxCount });

  return (
    <Grid container flexDirection="column" gap={2}>
      {showHeader && (
        <Typography variant="subtitle1" fontWeight={700}>
          {passOrderPage.label?.[language] || passOrderPage.id}
        </Typography>
      )}

      <Grid container alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {pickPrompt}
        </Typography>
        <Chip size="small" color={selectionInRange ? 'success' : 'default'} label={selectionChipLabel} />
      </Grid>

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {loading && (
        <Grid container justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Grid>
      )}

      {!loading &&
        availability.map((entry) => {
          const targetPage = getOrderPage(account, entry.order_page_id);
          const pageLabel = targetPage?.label?.[language] || targetPage?.label?.en || entry.order_page_id;
          return (
            <Box key={entry.order_page_id}>
              <Typography variant="subtitle2" fontWeight={700}>
                {pageLabel}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container flexDirection="column" gap={1}>
                {entry.availability.map((day) => (
                  <Grid key={day.weekday} container alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                      {getDayName(day.weekday, language)}
                    </Typography>
                    {day.slots.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                    {day.slots.map((slot) => {
                      const key = slotKey(entry.order_page_id, day.weekday, slot.startTime);
                      const isSelected = selectedKeys.has(key);
                      const label = `${formatSecondsToTime(slot.startTime, timeFormat)} · ${slot.holdersCount}/${
                        slot.capacity
                      }`;
                      if (slot.isDisabled && !isSelected) {
                        return (
                          <Chip
                            key={key}
                            size="small"
                            variant="outlined"
                            disabled
                            label={`${formatSecondsToTime(slot.startTime, timeFormat)} · ${t('passes.slot_full')}`}
                          />
                        );
                      }
                      return (
                        <Chip
                          key={key}
                          size="small"
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          onClick={() => toggleSlot(entry.order_page_id, day.weekday, slot.startTime)}
                          label={label}
                        />
                      );
                    })}
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
    </Grid>
  );
};
